import type { ProductAttributeLink, AttributeCondition, AttributeValue } from '@/core/types';

/**
 * Resolves the count a "repeat"/"max_select" rule should use for a triggering value: prefers the
 * admin-set numeric_value (set in Atrybuty — needed whenever the display label isn't itself a
 * number, e.g. options named "A"/"B"/"C" meant to mean 1/2/3), falling back to parsing the label
 * as an integer for older attributes that already relied on numeric labels ("1", "2", "3").
 */
function resolveCountFromValue(val: AttributeValue): number {
    if (val.numeric_value !== null && val.numeric_value !== undefined) {
        return val.numeric_value < 1 ? 1 : val.numeric_value;
    }
    const parsed = parseInt(val.value, 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

export interface ConfiguratorAttributeState {
    visible: boolean;
    allowedValueIds: Set<number> | null; // null == unrestricted (all of the attribute's values selectable)
    repeatCount: number; // 1 unless repeated by a source attribute rule
    maxSelectCount: number | null; // null == single select
}

export interface ConfiguratorEvaluation {
    byAttributeId: Record<number, ConfiguratorAttributeState>;
    priceDelta: number; // sum of price_modifier for every currently-selected AND currently-valid value
}

/**
 * Client-side mirror of the backend's authoritative
 * backend/shop/logic/products.py::compute_configurable_price visibility/pricing logic.
 */
export function evaluateConfigurator(
    selections: Record<number, number[]>,
    productAttributes: ProductAttributeLink[],
    conditions: AttributeCondition[],
    currency?: string,
): ConfiguratorEvaluation {
    const paByAttrId = new Map<number, ProductAttributeLink>();
    for (const pa of productAttributes) paByAttrId.set(pa.attribute_id, pa);

    const selectedByAttr: Record<number, number[]> = {};
    for (const [attrIdStr, valueIds] of Object.entries(selections || {})) {
        const attrId = Number(attrIdStr);
        if (valueIds && valueIds.length > 0) selectedByAttr[attrId] = valueIds;
    }

    // Visibility: An attribute with no incoming 'show' rule is visible by default.
    const attrsWithShowRules = new Set<number>();
    for (const c of conditions) {
        if (c.effect === 'show') {
            attrsWithShowRules.add(c.target_attribute_id);
        }
    }

    const visibleAttrIds = new Set<number>();
    for (const pa of productAttributes) {
        if (!attrsWithShowRules.has(pa.attribute_id)) {
            visibleAttrIds.add(pa.attribute_id);
        }
    }

    let changed = true;
    while (changed) {
        changed = false;
        for (const c of conditions) {
            if (c.effect !== 'show') continue;
            if (visibleAttrIds.has(c.target_attribute_id)) continue;
            if (!visibleAttrIds.has(c.source_attribute_id)) continue;

            const sourceSelected = selectedByAttr[c.source_attribute_id] || [];
            if (sourceSelected.some(v => (c.source_value_ids || []).includes(v))) {
                visibleAttrIds.add(c.target_attribute_id);
                changed = true;
            }
        }
    }

    // Value restriction: start with per-product allowed_value_ids (if set),
    // then apply intersection of all currently-active limit_values rules per target attribute.
    const allowedValuesByAttr = new Map<number, Set<number> | null>();
    for (const pa of productAttributes) {
        if (pa.allowed_value_ids && Array.isArray(pa.allowed_value_ids)) {
            allowedValuesByAttr.set(pa.attribute_id, new Set(pa.allowed_value_ids));
        }
    }
    const explicitRepeatByAttr = new Map<number, number>();
    const maxSelectByAttr = new Map<number, number>();

    for (const c of conditions) {
        if (!visibleAttrIds.has(c.source_attribute_id)) continue;
        const sourceSelected = selectedByAttr[c.source_attribute_id] || [];
        const isTriggered = sourceSelected.some(v => (c.source_value_ids || []).includes(v));
        if (!isTriggered) continue;

        if (c.effect === 'limit_values') {
            const allowed = new Set(c.target_value_ids || []);
            const existing = allowedValuesByAttr.get(c.target_attribute_id);
            if (existing === undefined || existing === null) {
                allowedValuesByAttr.set(c.target_attribute_id, allowed);
            } else {
                allowedValuesByAttr.set(c.target_attribute_id, new Set([...existing].filter(v => allowed.has(v))));
            }
        } else if (c.effect === 'repeat') {
            const sourcePa = paByAttrId.get(c.source_attribute_id);
            const selectedVid = sourceSelected[0];
            const selectedValObj = sourcePa?.attribute?.values?.find(v => v.id === selectedVid);
            if (selectedValObj) {
                explicitRepeatByAttr.set(c.target_attribute_id, resolveCountFromValue(selectedValObj));
            }
        } else if (c.effect === 'max_select') {
            const sourcePa = paByAttrId.get(c.source_attribute_id);
            const selectedVid = sourceSelected[0];
            const selectedValObj = sourcePa?.attribute?.values?.find(v => v.id === selectedVid);
            if (selectedValObj) {
                maxSelectByAttr.set(c.target_attribute_id, resolveCountFromValue(selectedValObj));
            }
        }
    }

    // Cascading repeater: When an attribute is revealed by a 'show' rule from a repeated
    // source attribute (e.g. GRUPY KOLORÓW #1 and #2 both picking "Beże"), its repeat count
    // should match the number of times that triggering value was selected across the active slots
    // of the source attribute, unless an explicit 'repeat' rule overrides it.
    const inheritedRepeatByAttr = new Map<number, number>();
    for (const c of conditions) {
        if (c.effect !== 'show') continue;
        if (!visibleAttrIds.has(c.source_attribute_id)) continue;
        if (!visibleAttrIds.has(c.target_attribute_id)) continue;

        const sourceSelected = selectedByAttr[c.source_attribute_id] || [];
        const sourceRepeat = explicitRepeatByAttr.get(c.source_attribute_id) ?? 1;
        const activeSourceSlots = sourceSelected.slice(0, sourceRepeat);

        const matchCount = activeSourceSlots.filter(v => (c.source_value_ids || []).includes(v)).length;
        if (matchCount > 0) {
            const prev = inheritedRepeatByAttr.get(c.target_attribute_id) || 0;
            inheritedRepeatByAttr.set(c.target_attribute_id, prev + matchCount);
        }
    }

    const byAttributeId: Record<number, ConfiguratorAttributeState> = {};
    for (const pa of productAttributes) {
        const isVisible = visibleAttrIds.has(pa.attribute_id);
        const explicitRepeat = explicitRepeatByAttr.get(pa.attribute_id);
        const inheritedRepeat = inheritedRepeatByAttr.get(pa.attribute_id);
        const repeatCount = explicitRepeat ?? (inheritedRepeat !== undefined && inheritedRepeat > 0 ? inheritedRepeat : 1);

        byAttributeId[pa.attribute_id] = {
            visible: isVisible,
            allowedValueIds: allowedValuesByAttr.get(pa.attribute_id) ?? null,
            repeatCount,
            maxSelectCount: maxSelectByAttr.get(pa.attribute_id) ?? null,
        };
    }

    // Price delta across all selected values (including multi-select and slot selections).
    let priceDelta = 0;
    const activeCurr = currency?.toUpperCase();
    for (const pa of productAttributes) {
        const state = byAttributeId[pa.attribute_id];
        if (!state.visible) continue;
        const valueIds = selectedByAttr[pa.attribute_id] || [];
        const activeCount = state.maxSelectCount && state.maxSelectCount > 1
            ? state.maxSelectCount
            : state.repeatCount;
        const activeValueIds = valueIds.slice(0, activeCount);

        for (const vid of activeValueIds) {
            if (state.allowedValueIds && !state.allowedValueIds.has(vid)) continue;
            const val = pa.attribute?.values?.find(v => v.id === vid);
            if (val) {
                if (activeCurr && val.currency_price_modifiers && val.currency_price_modifiers[activeCurr] !== undefined && val.currency_price_modifiers[activeCurr] !== null) {
                    priceDelta += Number(val.currency_price_modifiers[activeCurr]);
                } else if (val.resolved_price_modifier !== undefined && val.resolved_price_modifier !== null) {
                    // Backend-converted price_modifier for the active currency (see
                    // get_product_configurator) — used whenever the value has no explicit
                    // currency_price_modifiers override for it, so merchants aren't forced to
                    // fill in every value for every supported currency by hand.
                    priceDelta += val.resolved_price_modifier;
                } else {
                    priceDelta += val.price_modifier || 0;
                }
            }
        }
    }

    return { byAttributeId, priceDelta: Math.round(priceDelta * 100) / 100 };
}

/** True once every currently-visible required attribute has a valid, in-bounds selection. */
export function isConfiguratorComplete(
    selections: Record<number, number[]>,
    productAttributes: ProductAttributeLink[],
    evaluation: ConfiguratorEvaluation,
): boolean {
    for (const pa of productAttributes) {
        const state = evaluation.byAttributeId[pa.attribute_id];
        if (!state?.visible || !pa.required) continue;
        const valueIds = selections[pa.attribute_id] || [];
        const targetCount = state.maxSelectCount && state.maxSelectCount > 1
            ? 1
            : Math.max(1, state.repeatCount || 1);
        const validSelected = valueIds.slice(0, targetCount).filter(v => !state.allowedValueIds || state.allowedValueIds.has(v));
        if (validSelected.length < targetCount) return false;
    }
    return true;
}
